import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { getOrderByOrderNumber, getOrdersByEmail } from '../services/orders';
import { formatCurrency } from '../lib/currency';
import { useToast } from '../hooks/use-toast';

const OrderTrackingPage = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const validationSchema = Yup.object({
    orderNumber: Yup.string().required(t('validation.required')),
    email: Yup.string().email(t('validation.email')).required(t('validation.required')),
  });

  const formik = useFormik({
    initialValues: {
      orderNumber: '',
      email: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        // Try to find order by order number first
        const foundOrder = await getOrderByOrderNumber(values.orderNumber);

        // Verify email matches
        if (foundOrder && foundOrder.customerInfo?.email?.toLowerCase() === values.email.toLowerCase()) {
          setOrder(foundOrder);
        } else {
          // Try to find by email and order number combination
          const orders = await getOrdersByEmail(values.email);
          const matchingOrder = orders.find(
            (o) => o.orderNumber.toLowerCase() === values.orderNumber.toLowerCase()
          );

          if (matchingOrder) {
            setOrder(matchingOrder);
          } else {
            toast({
              title: t('order.notFound'),
              description: t('order.notFound'),
              variant: 'destructive',
            });
            setOrder(null);
          }
        }
      } catch (error) {
        console.error('Error fetching order:', error);
        toast({
          title: t('errors.generic'),
          description: t('order.notFound'),
          variant: 'destructive',
        });
        setOrder(null);
      } finally {
        setLoading(false);
      }
    },
  });

  const getStatusLabel = (status) => {
    const statusMap = {
      pending: t('order.status.pending'),
      paid: t('order.status.paid'),
      shipped: t('order.status.shipped'),
      delivered: t('order.status.delivered'),
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      pending: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-semibold mb-8">{t('order.trackOrder')}</h1>

        {/* Search Form */}
        <div className="bg-white rounded-lg p-6 border mb-8">
          <form onSubmit={formik.handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="orderNumber">{t('order.orderNumber')} *</Label>
              <Input
                id="orderNumber"
                name="orderNumber"
                placeholder="GP-YYYYMMDD-XXXXX"
                value={formik.values.orderNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={formik.errors.orderNumber && formik.touched.orderNumber ? 'border-red-500' : ''}
              />
              {formik.errors.orderNumber && formik.touched.orderNumber && (
                <p className="text-red-500 text-sm mt-1">{formik.errors.orderNumber}</p>
              )}
            </div>

            <div>
              <Label htmlFor="email">{t('checkout.email')} *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={formik.errors.email && formik.touched.email ? 'border-red-500' : ''}
              />
              {formik.errors.email && formik.touched.email && (
                <p className="text-red-500 text-sm mt-1">{formik.errors.email}</p>
              )}
            </div>

            <Button type="submit" disabled={loading} className="w-full">
              {loading ? t('common.loading') : t('order.trackOrder')}
            </Button>
          </form>
        </div>

        {/* Order Details */}
        {order && (
          <div className="bg-white rounded-lg p-6 border">
            {/* Order Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
              <div>
                <h2 className="text-2xl font-semibold mb-2">{t('order.orderNumber')}</h2>
                <p className="text-xl font-mono">{order.orderNumber}</p>
              </div>
              <div className="mt-4 sm:mt-0">
                <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                  {getStatusLabel(order.status)}
                </span>
              </div>
            </div>

            <Separator className="my-6" />

            {/* Order Info */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <h3 className="font-semibold mb-2">{t('order.orderDate')}</h3>
                <p className="text-gray-600">
                  {order.createdAt?.toDate
                    ? order.createdAt.toDate().toLocaleDateString('tr-TR')
                    : new Date().toLocaleDateString('tr-TR')}
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">{t('checkout.shippingInfo')}</h3>
                <div className="text-gray-600">
                  <p>{order.customerInfo?.fullName}</p>
                  <p>{order.customerInfo?.address}</p>
                  <p>
                    {order.customerInfo?.district} / {order.customerInfo?.city}
                  </p>
                  <p>{order.customerInfo?.phone}</p>
                </div>
              </div>
            </div>

            <Separator className="my-6" />

            {/* Order Items */}
            <div className="mb-6">
              <h3 className="font-semibold mb-4">{t('checkout.orderSummary')}</h3>
              <div className="space-y-3">
                {order.items?.map((item, index) => (
                  <div key={index} className="flex gap-3">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-16 h-20 object-cover rounded"
                    />
                    <div className="flex-1">
                      <p className="font-medium">{item.name}</p>
                      <p className="text-sm text-gray-500">
                        {t('product.size')}: {item.size} | {t('product.color')}: {item.color}
                      </p>
                      <p className="text-sm">
                        {item.quantity} x {formatCurrency(item.price)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Separator className="my-6" />

            {/* Totals */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">{t('cart.subtotal')}</span>
                <span>{formatCurrency(order.subtotal || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('cart.shipping')}</span>
                <span>{formatCurrency(order.shipping || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('cart.tax')}</span>
                <span>{formatCurrency(order.tax || 0)}</span>
              </div>
              <Separator className="my-2" />
              <div className="flex justify-between font-semibold text-lg">
                <span>{t('cart.total')}</span>
                <span>{formatCurrency(order.total || 0)}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default OrderTrackingPage;
